import type { MyPublicFolder } from '@platform/api-client';
import { usePagedList, type PagedList } from '@platform/ui';
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api, PAGE_SIZE } from './api';

const folderId = (folder: MyPublicFolder) => folder.id;
const MAX_SAVED_PATHS = 50;
const MAX_SAVED_PATH_DEPTH = 16;
const MAX_PAGES_PER_PARENT = 4;
const MAX_FOLDER_REQUESTS = 4;
type FolderPage = Awaited<ReturnType<typeof api.listPublicFolders>>;

function uniqueFolders(folders: readonly MyPublicFolder[]): MyPublicFolder[] {
  return [...new Map(folders.map(folder => [folder.id, folder])).values()];
}

export interface PublicFolderState {
  folders: PagedList<MyPublicFolder>;
  selected: MyPublicFolder | undefined;
  path: readonly MyPublicFolder[];
  refreshVersion: number;
  refresh: () => void;
  select: (folder: MyPublicFolder) => void;
  enter: (folder: MyPublicFolder) => void;
  back: () => void;
  childrenByParent: ReadonlyMap<string, readonly MyPublicFolder[]>;
  expandedIds: ReadonlySet<string>;
  loadingParentIds: ReadonlySet<string>;
  failedParentIds: ReadonlySet<string>;
  hasMoreByParent: ReadonlySet<string>;
  toggleExpanded: (folder: MyPublicFolder) => void;
  loadChildren: (folder: MyPublicFolder) => void;
  loadMoreChildren: (folder: MyPublicFolder) => void;
}
const PublicFolderContext = createContext<PublicFolderState | undefined>(undefined);

/** Server-paged user hierarchy; UI selection never grants content access. */
export function PublicFolderProvider({ active, children, savedPaths = [], onDiscoverPath }: { active: boolean; children: ReactNode; savedPaths?: readonly (readonly string[])[]; onDiscoverPath?: (folderId: string, path: readonly string[]) => void }) {
  const [path, setPath] = useState<MyPublicFolder[]>([]);
  const [selected, setSelected] = useState<MyPublicFolder>();
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [childrenByParent, setChildrenByParent] = useState<Map<string, MyPublicFolder[]>>(new Map());
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [loadingParentIds, setLoadingParentIds] = useState<Set<string>>(new Set());
  const [failedParentIds, setFailedParentIds] = useState<Set<string>>(new Set());
  const [hasMoreByParent, setHasMoreByParent] = useState<Set<string>>(new Set());
  const [nextOffsets, setNextOffsets] = useState<Map<string, number>>(new Map());
  const requestGeneration = useRef(0);
  const wasActive = useRef(active);
  const inFlightChildren = useRef(new Map<string, Promise<FolderPage | undefined>>());
  const pageCache = useRef(new Map<string, FolderPage>());
  const inFlightPages = useRef(new Map<string, Promise<FolderPage | undefined>>());
  const requestCountsByParent = useRef(new Map<string, number>());
  const activeRequests = useRef(0);
  const waitingRequests = useRef<Array<() => void>>([]);
  const discoveredPaths = useRef(new Map<string, readonly string[]>());
  const onDiscoverPathRef = useRef(onDiscoverPath);
  const savedPathRun = useRef(0);
  const currentPath = useRef(path);
  useEffect(() => {
    onDiscoverPathRef.current = onDiscoverPath;
    currentPath.current = path;
  }, [onDiscoverPath, path]);
  const parent = path.at(-1)?.id;

  const fetchPage = useCallback((parentId: string | undefined, offset: number, bounded = false): Promise<FolderPage | undefined> => {
    if (!active) return Promise.resolve(undefined);
    const generation = requestGeneration.current;
    const key = JSON.stringify([generation, parentId ?? null, offset]);
    const cached = pageCache.current.get(key);
    if (cached) return Promise.resolve(cached);
    const pending = inFlightPages.current.get(key);
    if (pending) return pending;
    const parentKey = parentId ?? '';
    const attempts = requestCountsByParent.current.get(parentKey) ?? 0;
    if (bounded && attempts >= MAX_PAGES_PER_PARENT) return Promise.resolve(undefined);
    requestCountsByParent.current.set(parentKey, attempts + 1);

    const acquire = async () => {
      if (activeRequests.current < MAX_FOLDER_REQUESTS) {
        activeRequests.current++;
        return;
      }
      await new Promise<void>(resolve => waitingRequests.current.push(resolve));
    };
    const release = () => {
      const next = waitingRequests.current.shift();
      if (next) next();
      else activeRequests.current--;
    };
    const request = (async () => {
      await acquire();
      try {
        if (generation !== requestGeneration.current) return undefined;
        const page = await api.listPublicFolders({ parent: parentId, offset, limit: PAGE_SIZE });
        if (generation !== requestGeneration.current) return undefined;
        pageCache.current.set(key, page);
        return page;
      } finally {
        release();
      }
    })();
    inFlightPages.current.set(key, request);
    void request.then(() => { if (inFlightPages.current.get(key) === request) inFlightPages.current.delete(key); }, () => { if (inFlightPages.current.get(key) === request) inFlightPages.current.delete(key); });
    return request;
  }, [active]);

  const reportPage = useCallback((parentId: string | undefined, page: FolderPage) => {
    const prefix = parentId ? discoveredPaths.current.get(parentId) : [];
    if (parentId && !prefix) return;
    for (const folder of page.folders) {
      const folderPath = [...(prefix ?? []), folder.id];
      const previous = discoveredPaths.current.get(folder.id);
      discoveredPaths.current.set(folder.id, folderPath);
      if (!previous || previous.length !== folderPath.length || previous.some((id, index) => id !== folderPath[index])) {
        onDiscoverPathRef.current?.(folder.id, folderPath);
      }
    }
  }, []);

  const storeRootPage = useCallback((page: FolderPage, offset: number) => {
    reportPage(undefined, page);
    setChildrenByParent(current => {
      const previous = offset ? current.get('') ?? [] : [];
      return new Map(current).set('', uniqueFolders([...previous, ...page.folders]));
    });
    setNextOffsets(current => { const next = new Map(current); if (page.nextOffset === null) next.delete(''); else next.set('', page.nextOffset ?? offset + page.folders.length); return next; });
    setHasMoreByParent(current => { const next = new Set(current); if (page.nextOffset === null) next.delete(''); else next.add(''); return next; });
  }, [reportPage]);

  const folders = usePagedList(async offset => {
    if (!active) return { items: [], total: 0, nextOffset: null };
    const generation = requestGeneration.current;
    const requestedPath = path.map(folder => folder.id);
    const page = await fetchPage(parent, offset);
    if (page && generation === requestGeneration.current && currentPath.current.map(folder => folder.id).join('\u0000') === requestedPath.join('\u0000')) reportPage(parent, page);
    return { items: page?.folders ?? [], total: page?.total ?? 0, nextOffset: page?.nextOffset ?? null };
  }, JSON.stringify([active, parent, refreshVersion]), folderId);

  const requestChildren = useCallback((folder: MyPublicFolder, offset: number, bounded = false): Promise<FolderPage | undefined> => {
    if (!active) return Promise.resolve(undefined);
    const generation = requestGeneration.current;
    const key = JSON.stringify([folder.id, offset]);
    const pending = inFlightChildren.current.get(key);
    if (pending) return pending;
    setLoadingParentIds(current => new Set(current).add(folder.id));
    const request = (async () => {
      try {
        const page = await fetchPage(folder.id, offset, bounded);
        if (!page || generation !== requestGeneration.current) return undefined;
        reportPage(folder.id, page);
        setFailedParentIds(current => { const next = new Set(current); next.delete(folder.id); return next; });
        setChildrenByParent(current => {
          const merged = offset ? [...(current.get(folder.id) ?? []), ...page.folders] : page.folders;
          return new Map(current).set(folder.id, uniqueFolders(merged));
        });
        setNextOffsets(current => { const next = new Map(current); if (page.nextOffset === null) next.delete(folder.id); else next.set(folder.id, page.nextOffset ?? offset + page.folders.length); return next; });
        setHasMoreByParent(current => { const next = new Set(current); if (page.nextOffset === null) next.delete(folder.id); else next.add(folder.id); return next; });
        return page;
      } catch {
        if (generation === requestGeneration.current) setFailedParentIds(current => new Set(current).add(folder.id));
        return undefined;
      } finally {
        if (generation === requestGeneration.current) {
          setLoadingParentIds(current => { const next = new Set(current); next.delete(folder.id); return next; });
        }
      }
    })();
    inFlightChildren.current.set(key, request);
    void request.then(() => { if (inFlightChildren.current.get(key) === request) inFlightChildren.current.delete(key); }, () => { if (inFlightChildren.current.get(key) === request) inFlightChildren.current.delete(key); });
    return request;
  }, [active, fetchPage, reportPage]);

  const loadChildren = useCallback((folder: MyPublicFolder) => {
    if (!childrenByParent.has(folder.id) && !loadingParentIds.has(folder.id)) void requestChildren(folder, 0);
  }, [childrenByParent, loadingParentIds, requestChildren]);
  const loadMoreChildren = useCallback((folder: MyPublicFolder) => {
    const offset = nextOffsets.get(folder.id);
    if (offset !== undefined && !loadingParentIds.has(folder.id)) void requestChildren(folder, offset);
  }, [loadingParentIds, nextOffsets, requestChildren]);
  const toggleExpanded = useCallback((folder: MyPublicFolder) => {
    setExpandedIds(current => { const next = new Set(current); if (next.has(folder.id)) next.delete(folder.id); else next.add(folder.id); return next; });
    if (!expandedIds.has(folder.id)) loadChildren(folder);
  }, [expandedIds, loadChildren]);
  const refresh = useCallback(() => {
    const expandedFolders = [...expandedIds].map(id => {
      for (const folder of folders.items) if (folder.id === id) return folder;
      for (const children of childrenByParent.values()) for (const folder of children) if (folder.id === id) return folder;
      return undefined;
    }).filter((folder): folder is MyPublicFolder => Boolean(folder));
    requestGeneration.current++;
    savedPathRun.current++;
    pageCache.current.clear();
    inFlightPages.current.clear();
    inFlightChildren.current.clear();
    requestCountsByParent.current.clear();
    discoveredPaths.current.clear();
    setChildrenByParent(new Map());
    setNextOffsets(new Map());
    setHasMoreByParent(new Set());
    setFailedParentIds(new Set());
    setLoadingParentIds(new Set());
    setRefreshVersion(value => value + 1);
    for (const folder of expandedFolders) void requestChildren(folder, 0);
  }, [childrenByParent, expandedIds, folders.items, requestChildren]);

  useLayoutEffect(() => {
    if (wasActive.current === active) return;
    wasActive.current = active;
    requestGeneration.current++;
    savedPathRun.current++;
    pageCache.current.clear();
    inFlightPages.current.clear();
    inFlightChildren.current.clear();
    requestCountsByParent.current.clear();
    discoveredPaths.current.clear();
    // Reactivation must clear cached branches before passive page loaders run.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (active) refresh();
  }, [active, refresh]);

  const normalizedSavedPaths = useMemo(() => {
    const unique = new Map<string, readonly string[]>();
    for (const candidate of savedPaths.slice(0, MAX_SAVED_PATHS)) {
      if (!Array.isArray(candidate) || candidate.length < 1 || candidate.length > MAX_SAVED_PATH_DEPTH || candidate.some(id => typeof id !== 'string' || !id.trim())) continue;
      unique.set(JSON.stringify(candidate), candidate);
    }
    return [...unique.values()];
  }, [savedPaths]);
  const savedPathsKey = JSON.stringify(normalizedSavedPaths);
  useEffect(() => {
    const run = ++savedPathRun.current;
    const pathsForRun = JSON.parse(savedPathsKey) as string[][];
    if (!active || !pathsForRun.length) return;
    let cancelled = false;
    const generation = requestGeneration.current;
    const isCurrent = () => !cancelled && run === savedPathRun.current && generation === requestGeneration.current;
    const discoverPath = async (ids: readonly string[]) => {
      let offset = 0;
      let currentFolder: MyPublicFolder | undefined;
      for (let pageIndex = 0; pageIndex < MAX_PAGES_PER_PARENT; pageIndex++) {
        const page = await fetchPage(undefined, offset, true);
        if (!isCurrent() || !page) return;
        storeRootPage(page, offset);
        currentFolder = page.folders.find(folder => folder.id === ids[0]);
        if (currentFolder) break;
        if (page.nextOffset === null) return;
        offset = page.nextOffset ?? offset + page.folders.length;
      }
      if (!currentFolder || !isCurrent()) return;
      let currentPath = [currentFolder.id];
      discoveredPaths.current.set(currentFolder.id, currentPath);
      for (let index = 1; index < ids.length; index++) {
        let nextFolder: MyPublicFolder | undefined;
        offset = 0;
        for (let pageIndex = 0; pageIndex < MAX_PAGES_PER_PARENT; pageIndex++) {
          const page = await requestChildren(currentFolder, offset, true);
          if (!isCurrent() || !page) return;
          nextFolder = page.folders.find(folder => folder.id === ids[index]);
          if (nextFolder) break;
          if (page.nextOffset === null) return;
          offset = page.nextOffset ?? offset + page.folders.length;
        }
        if (!nextFolder || !isCurrent()) return;
        currentFolder = nextFolder;
        currentPath = [...currentPath, currentFolder.id];
        discoveredPaths.current.set(currentFolder.id, currentPath);
      }
    };
    const runWorkers = async () => {
      let nextIndex = 0;
      const worker = async () => {
        while (isCurrent()) {
          const index = nextIndex++;
          if (index >= pathsForRun.length) return;
          try { await discoverPath(pathsForRun[index]!); } catch { /* Unavailable paths stay generic. */ }
        }
      };
      await Promise.all(Array.from({ length: Math.min(MAX_FOLDER_REQUESTS, pathsForRun.length) }, () => worker()));
    };
    void runWorkers();
    return () => { cancelled = true; };
  }, [active, fetchPage, savedPathsKey, refreshVersion, requestChildren, storeRootPage]);

  const rootCached = useMemo(() => childrenByParent.get('') ?? [], [childrenByParent]);
  const visibleFolders = useMemo<PagedList<MyPublicFolder>>(() => parent ? folders : { ...folders, items: uniqueFolders([...folders.items, ...rootCached]) }, [folders, parent, rootCached]);
  const discoveredChildren = useMemo(() => new Map(childrenByParent).set('', parent ? rootCached : uniqueFolders([...folders.items, ...rootCached])), [childrenByParent, folders.items, parent, rootCached]);
  const treeHasMore = useMemo(() => {
    const result = new Set(hasMoreByParent);
    if (!parent && (folders.hasMore || hasMoreByParent.has(''))) result.add('');
    else result.delete('');
    return result;
  }, [hasMoreByParent, folders.hasMore, parent]);

  return <PublicFolderContext.Provider value={{ folders: visibleFolders, selected, path, refreshVersion, refresh, select: setSelected,
    enter: folder => { setPath(current => current.at(-1)?.id === folder.id ? current : [...current, folder]); setSelected(folder); },
    back: () => { setPath(current => current.slice(0, -1)); setSelected(undefined); },
    childrenByParent: discoveredChildren, expandedIds, loadingParentIds, failedParentIds, hasMoreByParent: treeHasMore, toggleExpanded, loadChildren, loadMoreChildren,
  }}>{children}</PublicFolderContext.Provider>;
}

export function usePublicFolders(): PublicFolderState {
  const value = useContext(PublicFolderContext);
  if (!value) throw new Error('PublicFolderProvider required');
  return value;
}
